import { useEffect, useState, useCallback } from 'react'
import { listAdminBookings, patchAdminBooking } from '../lib/api.js'
import ErrorAlert from '../components/ErrorAlert.jsx'
import { PAGE_STANDARD } from '../lib/layout.js'

const statusOptions = ['PENDING', 'ACCEPTED', 'DECLINED', 'CLOSED']

const statusBadge = (status) => {
  const map = {
    PENDING: 'bg-amber-100 text-amber-800',
    ACCEPTED: 'bg-emerald-100 text-emerald-800',
    DECLINED: 'bg-rose-100 text-rose-800',
    CLOSED: 'bg-slate-200 text-slate-700',
  }
  return map[status] || 'bg-slate-100 text-slate-700'
}

export default function AdminBookingsPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    listAdminBookings()
      .then(setRows)
      .catch((err) => setError(err.message || String(err)))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const updateStatus = async (id, status) => {
    try {
      const updated = await patchAdminBooking(id, { status })
      setRows((prev) => prev.map((r) => (r.id === id ? updated : r)))
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          <span className="brand-gradient-text">Bookings</span>
        </h1>
        <p className="mt-2 text-base text-slate-600">
          Speaker requests submitted via <code>/book</code>. Newest first.
        </p>
      </header>

      {error ? (
        <ErrorAlert title="Couldn't load bookings" message={error} source="AdminBookingsPage" />
      ) : null}

      {loading ? (
        <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200/60">
          <h3 className="text-base font-semibold text-slate-900">No bookings yet</h3>
          <p className="mt-1 text-sm text-slate-500">When someone submits the form, requests will appear here.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((b) => (
            <li key={b.id} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/60 sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold leading-snug tracking-tight text-slate-900">
                    {b.eventName}
                  </h2>
                  <div className="mt-0.5 text-sm text-slate-500">
                    {b.organiserName} · <a href={`mailto:${b.organiserEmail}`} className="text-brand-700 hover:underline">{b.organiserEmail}</a>
                    {b.organiserOrg ? <> · {b.organiserOrg}</> : null}
                  </div>
                </div>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadge(b.status)}`}>
                  {b.status}
                </span>
              </div>

              <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
                {b.startsOn ? <div><dt className="inline text-slate-500">Dates: </dt><dd className="inline">{b.startsOn}{b.endsOn && b.endsOn !== b.startsOn ? ` → ${b.endsOn}` : ''}</dd></div> : null}
                {(b.city || b.country) ? <div><dt className="inline text-slate-500">Where: </dt><dd className="inline">{[b.city, b.country].filter(Boolean).join(', ')}</dd></div> : null}
                {b.format ? <div><dt className="inline text-slate-500">Format: </dt><dd className="inline">{b.format.toLowerCase().replace('_', '-')}</dd></div> : null}
                {b.audienceSize != null ? <div><dt className="inline text-slate-500">Audience: </dt><dd className="inline">{b.audienceSize}</dd></div> : null}
                {b.speakerUserIds?.length ? <div><dt className="inline text-slate-500">Speakers: </dt><dd className="inline">{b.speakerUserIds.join(', ')}</dd></div> : null}
                {b.honorariumAmount != null ? <div><dt className="inline text-slate-500">Honorarium: </dt><dd className="inline">{b.honorariumAmount} {b.honorariumCurrency}</dd></div> : null}
              </dl>

              {b.topic ? <p className="mt-3 text-sm text-slate-700"><span className="text-slate-500">Topic:</span> {b.topic}</p> : null}
              {b.notes ? <p className="mt-2 text-sm text-slate-700"><span className="text-slate-500">Notes:</span> {b.notes}</p> : null}

              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
                <label className="inline-flex items-center gap-2">
                  <span className="text-slate-500">Status</span>
                  <select
                    value={b.status}
                    onChange={(e) => updateStatus(b.id, e.target.value)}
                    className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
                  >
                    {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <span className="text-xs text-slate-400">Submitted {new Date(b.createdAt).toLocaleString()}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </main>
  )
}
